import React, { useState } from 'react';
import { css } from '@emotion/core';
import {
  IconButton,
  Modal,
  TextButton,
  TrashIcon,
  Text,
  useModalControl,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import ReactTable from 'react-table';
import format from 'date-fns/format';

import { HandoffSnapshot } from '../../handoff-hub.types';
import { TableStyles } from '../../../../../styles/table-style';
import {
  handoffTypesDisplayNamesMap,
  HandoffTypes,
} from './edit-handoff/handoff-metrics/handoff.constants';
import { ReactComponent as StatusWarningIcon } from 'images/status-warning.svg';

type Props = {
  snapshots?: HandoffSnapshot[];
  onViewOrEditClick: (snapshotId: string, groupId?: string) => void;
  onDeleteClick: (snapshotId: string) => void;
};

export const HandoffTable = ({
  snapshots = [],
  onViewOrEditClick,
  onDeleteClick,
}: Props) => {
  const [snapshotToDelete, setSnapshotToDelete] = useState<HandoffSnapshot>();

  const deleteHandoffSnapshotModalControl = useModalControl();

  return (
    <>
      <ReactTable
        columns={[
          {
            Header: 'Handoff Name',
            id: 'handoffName',
            accessor: (data) => (
              <Text css={{ whiteSpace: 'pre-wrap' }}>
                {data.handoffName.length > 0 ? data.handoffName : data.handoffType}
              </Text>
            ),
          },
          {
            Header: 'Type',
            id: 'handoffType',
            accessor: (data) => (
              <Text>{handoffTypesDisplayNamesMap[data.handoffType]}</Text>
            ),
          },
          {
            Header: 'Date Submitted',
            id: 'submittedAt',
            accessor: (data) => (
              <Text>
                {data.submittedAt
                  ? format(new Date(data.submittedAt), 'MM/dd/yy')
                  : 'Draft'}
              </Text>
            ),
            width: 150,
          },
          {
            Header: 'Submitted By',
            id: 'handoffSubmittedBy',
            accessor: (data) => <Text>{data.handoffSubmittedBy ?? ''}</Text>,
          },
          {
            Header: 'Details',
            id: 'details',
            accessor: (data) => {
              const isWie = data.handoffType === HandoffTypes.WeaveInsuranceEligibility;
              const hasException =
                isWie &&
                data.metrics?.some((m) => m.exception && m.exception.trim().length > 0);
              return (
                <div css={{ display: 'flex', alignItems: 'center' }}>
                  {hasException && (
                    <span css={exceptionNotedBadge}>
                      <span css={warningBullet}>
                        <StatusWarningIcon css={{ width: 16, height: 16 }} />
                      </span>
                      Exception Noted
                    </span>
                  )}
                </div>
              );
            },
            width: 150,
          },
          {
            id: 'action',
            accessor: (data) => (
              <div css={{ display: 'flex' }}>
                {!data.submittedAt && (
                  <IconButton
                    label="Delete"
                    onClick={() => {
                      setSnapshotToDelete(data);
                      deleteHandoffSnapshotModalControl.openModal();
                    }}
                  >
                    <TrashIcon />
                  </IconButton>
                )}
                <TextButton
                  css={buttonStyles}
                  onClick={() => onViewOrEditClick(data.id!, data.groupId)}
                >
                  {data.submittedAt ? 'View' : 'Edit'}
                </TextButton>
              </div>
            ),
            width: 150,
          },
        ]}
        data={snapshots}
        showPagination={true}
        css={[TableStyles, actionCell]}
      />

      <Modal {...deleteHandoffSnapshotModalControl.modalProps} maxWidth={480}>
        <Modal.Header>Delete Handoff Draft</Modal.Header>
        <Modal.Body css={modalBodyStyles}>
          <Text size="large">Are you sure you want to delete this handoff draft?</Text>
          {snapshotToDelete?.handoffType === HandoffTypes.OnboardingHandoffUnify && (
            <Text size="medium" color="error">
              This draft is shared across the locations grouped in this Unify handoff.
              Deleting it here only removes it for this location - it will remain visible
              in the other grouped locations.
            </Text>
          )}
        </Modal.Body>
        <Modal.Actions
          onPrimaryClick={() => {
            onDeleteClick(snapshotToDelete?.id ?? '');
            deleteHandoffSnapshotModalControl.closeModal();
          }}
          primaryLabel="Delete"
          onSecondaryClick={deleteHandoffSnapshotModalControl.closeModal}
        />
      </Modal>
    </>
  );
};

const actionCell = css`
  .rt-td:last-child {
    display: flex;
    justify-content: flex-end;
  }
`;

const buttonStyles = css`
  color: ${theme.colors.weaveBlue} !important;
`;

const exceptionNotedBadge = css`
  display: inline-flex;
  align-items: center;
  background-color: #fcf4e4;
  border: 1px solid #eec085;
  border-radius: 4px;
  color: #8c5b1b;
  padding: ${theme.spacing(0.5, 1)};
  font-size: 12px;
  font-weight: 500;
`;

const warningBullet = css`
  margin-right: ${theme.spacing(0.5)};
  font-weight: bold;
  display: flex;
  align-items: center;
`;

const modalBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;
